import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../lib/auth';
import { toggleInSet } from '../lib/collections';
import type { MediaItem } from './mediaSlice';

/**
 * favouritesSlice — the current internal user's single, private Favourites set.
 *
 * Mirrors `mediaSlice` (createAsyncThunk + apiClient + selectors). The star
 * control on media cards / list rows / the detail page dispatches an optimistic
 * `optimisticSetFavourite` reducer that applies immediately, then the
 * add/remove thunk. On a rejected thunk the previous `favouriteIds` value is
 * restored and an error is surfaced so the displayed state stays consistent
 * with the stored state (R12.2).
 *
 * Requirements: 3.1, 3.6, 12.1, 12.2
 */

export interface FavouritesState {
  items: MediaItem[];
  loading: boolean;
  error: string | null;
  /** ids of the media in the user's Favourites set (serialisable as string[]). */
  favouriteIds: string[];
}

const initialState: FavouritesState = {
  items: [],
  loading: false,
  error: null,
  favouriteIds: [],
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

interface FavouritesListResponse {
  items: MediaItem[];
}

/** GET /favourites → list the user's favourited media. */
export const fetchFavourites = createAsyncThunk(
  'favourites/fetch',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<FavouritesListResponse>('/favourites');
      return response.data.items ?? [];
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load favourites';
      return rejectWithValue(message);
    }
  },
);

/** POST /favourites { mediaId } → add a favourite (idempotent). */
export const addFavourite = createAsyncThunk(
  'favourites/add',
  async (mediaId: string, { rejectWithValue }) => {
    try {
      await apiClient.post('/favourites', { mediaId });
      return mediaId;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add favourite';
      return rejectWithValue({ mediaId, message });
    }
  },
);

/** DELETE /favourites/{mediaId} → remove a favourite (idempotent). */
export const removeFavourite = createAsyncThunk(
  'favourites/remove',
  async (mediaId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/favourites/${mediaId}`);
      return mediaId;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to remove favourite';
      return rejectWithValue({ mediaId, message });
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const favouritesSlice = createSlice({
  name: 'favourites',
  initialState,
  reducers: {
    /**
     * Optimistically apply a favourite toggle. Updates `favouriteIds`
     * immediately via the pure `toggleInSet`. When `next` is false the item is
     * also dropped from `items` so the Favourites page reacts to unstarring
     * (R3.6).
     */
    optimisticSetFavourite(state, action: PayloadAction<{ mediaId: string; next: boolean }>) {
      const { mediaId, next } = action.payload;
      state.favouriteIds = Array.from(toggleInSet(state.favouriteIds, mediaId, next));
      const item = state.items.find((i) => i.mediaId === mediaId);
      if (item) {
        item.isFavourite = next;
      }
      if (!next) {
        state.items = state.items.filter((i) => i.mediaId !== mediaId);
      }
    },
  },
  extraReducers: (builder) => {
    // fetchFavourites
    builder
      .addCase(fetchFavourites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFavourites.fulfilled, (state, action) => {
        state.items = action.payload.map((item) => ({ ...item, isFavourite: true }));
        state.favouriteIds = action.payload.map((item) => item.mediaId);
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchFavourites.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to load favourites';
      });

    // addFavourite — optimistic apply already happened; only handle rollback.
    builder.addCase(addFavourite.rejected, (state, action) => {
      const payload = action.payload as { mediaId: string; message: string } | undefined;
      if (payload) {
        // Roll back: the optimistic add set it true, so remove it again.
        state.favouriteIds = Array.from(toggleInSet(state.favouriteIds, payload.mediaId, false));
        state.error = payload.message ?? 'Failed to add favourite';
      } else {
        state.error = 'Failed to add favourite';
      }
    });

    // removeFavourite — optimistic apply already happened; only handle rollback.
    builder.addCase(removeFavourite.rejected, (state, action) => {
      const payload = action.payload as { mediaId: string; message: string } | undefined;
      if (payload) {
        // Roll back: the optimistic remove set it false, so add it back.
        state.favouriteIds = Array.from(toggleInSet(state.favouriteIds, payload.mediaId, true));
        state.error = payload.message ?? 'Failed to remove favourite';
      } else {
        state.error = 'Failed to remove favourite';
      }
    });
  },
});

export const { optimisticSetFavourite } = favouritesSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectFavourites = (state: { favourites: FavouritesState }) => state.favourites.items;
export const selectFavouritesLoading = (state: { favourites: FavouritesState }) =>
  state.favourites.loading;
export const selectFavouritesError = (state: { favourites: FavouritesState }) =>
  state.favourites.error;
export const selectFavouriteIds = (state: { favourites: FavouritesState }) =>
  state.favourites.favouriteIds;

/** Factory selector: is a given mediaId currently favourited? */
export const selectIsFavourite =
  (mediaId: string) =>
  (state: { favourites: FavouritesState }): boolean =>
    state.favourites.favouriteIds.includes(mediaId);

export default favouritesSlice.reducer;
